import { useState, useEffect } from "react";
import axios from "axios";
import { 
  FileText, 
  History, 
  Gauge, 
  Settings, 
  PoundSterling,
  CheckCircle,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  TrendingUp,
  Crown,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const EXTRA_ICONS = {
  history_check: Shield,
  mot_history: History,
  mileage: Gauge,
  specs: Settings,
  valuation: PoundSterling
};

function VehicleExtras({ registration, user, isPremium }) {
  const [availableExtras, setAvailableExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasedData, setPurchasedData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAvailableExtras();
  }, []);

  useEffect(() => {
    if (registration && user) {
      fetchPurchasedExtras();
      
      // Check if returning from successful payment
      const urlParams = new URLSearchParams(window.location.search);
      const successPurchaseId = urlParams.get('extras_success');
      const successRegistration = urlParams.get('registration');
      
      if (successPurchaseId && successRegistration === registration) {
        // Confirm the payment and fetch data
        confirmPaymentAndFetchData(successPurchaseId);
        // Clean URL
        window.history.replaceState({}, document.title, '/dashboard');
      }
    }
  }, [registration, user]);

  const confirmPaymentAndFetchData = async (purchaseId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/vehicle/extras/confirm-payment`,
        null,
        {
          params: { purchase_id: purchaseId },
          withCredentials: true
        }
      );
      
      if (response.data.success) {
        setPurchasedData(response.data);
        toast.success("Purchase complete! Your vehicle data is ready.");
      }
    } catch (err) {
      console.error("Error confirming payment:", err);
      toast.error("Error loading purchased data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableExtras = async () => {
    try {
      const response = await axios.get(`${API}/vehicle/extras/available`);
      setAvailableExtras(response.data.extras);
    } catch (err) {
      console.error("Error fetching extras:", err);
    }
  };

  const fetchPurchasedExtras = async () => {
    try {
      const response = await axios.get(
        `${API}/vehicle/extras/purchased/${registration}`,
        { withCredentials: true }
      );
      if (response.data.purchases && response.data.purchases.length > 0) {
        setPurchasedData(response.data.purchases[0]);
      }
    } catch (err) {
      console.error("Error fetching purchased extras:", err);
    }
  };

  const toggleExtra = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const calculateTotal = () => {
    return selectedExtras.reduce((total, extraId) => {
      const extra = availableExtras.find(e => e.id === extraId);
      return total + (extra?.price || 0);
    }, 0);
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error("Please sign in to purchase extras");
      return;
    }

    if (selectedExtras.length === 0) {
      toast.error("Please select at least one extra");
      return;
    }

    setPurchasing(true);
    setError(null);

    try {
      // Create Stripe Checkout session
      const response = await axios.post(
        `${API}/vehicle/extras/create-payment`,
        {
          registration: registration,
          extras: selectedExtras
        },
        { withCredentials: true }
      );

      const { checkout_url } = response.data;

      if (checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }

    } catch (err) {
      console.error("Purchase error:", err);
      setError(err.response?.data?.detail || err.message || "Payment failed");
      toast.error("Payment failed. Please try again.");
      setPurchasing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatPrice = (pence) => {
    if (pence >= 100) {
      return `£${(pence / 100).toFixed(2)}`;
    }
    return `${pence}p`;
  };

  const renderExtraData = (type, data) => {
    if (!data) {
      return (
        <div className="text-gray-500 text-sm p-4">
          No data available for this extra.
        </div>
      );
    }

    switch (type) {
      case "history_check":
        return (
          <div className="space-y-3 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${data.stolen ? 'bg-red-500' : 'bg-green-500'}`}></span>
                <span>Stolen: {data.stolen ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${data.scrapped ? 'bg-red-500' : 'bg-green-500'}`}></span>
                <span>Scrapped: {data.scrapped ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${data.writeOff ? 'bg-red-500' : 'bg-green-500'}`}></span>
                <span>Write-off: {data.writeOff ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${data.financeOutstanding ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                <span>Finance: {data.financeOutstanding ? 'Outstanding' : 'Clear'}</span>
              </div>
            </div>
            {data.previousKeepers !== undefined && (
              <div className="text-sm text-gray-600">
                Previous Keepers: {data.previousKeepers}
              </div>
            )}
            {data.plateChanges && data.plateChanges.length > 0 && (
              <div className="mt-2">
                <h4 className="font-medium text-sm mb-1">Plate Changes:</h4>
                <ul className="text-sm text-gray-600">
                  {data.plateChanges.map((change, i) => (
                    <li key={i}>{change.from} → {change.to} ({change.date})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case "mot_history":
        return (
          <div className="space-y-3 p-4 max-h-80 overflow-y-auto">
            {Array.isArray(data.motTests) ? (
              data.motTests.map((test, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      test.testResult === 'PASSED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {test.testResult}
                    </span>
                    <span className="text-sm text-gray-500">{test.completedDate}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Mileage: {test.odometerValue?.toLocaleString()} {test.odometerUnit}
                  </div>
                  {test.defects && test.defects.length > 0 && (
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-gray-500">Advisories/Defects:</h5>
                      <ul className="text-xs text-gray-600 mt-1">
                        {test.defects.map((defect, j) => (
                          <li key={j} className="flex items-start gap-1">
                            <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                              defect.type === 'ADVISORY' ? 'bg-yellow-400' : 'bg-red-400'
                            }`}></span>
                            {defect.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <pre className="text-xs overflow-auto bg-gray-50 p-3 rounded">{JSON.stringify(data, null, 2)}</pre>
            )}
          </div>
        );

      case "mileage":
        return (
          <div className="p-4">
            {Array.isArray(data.mileageRecords) ? (
              <div className="space-y-2">
                {data.mileageRecords.map((record, i) => (
                  <div key={i} className="flex justify-between items-center border-b pb-2">
                    <span className="text-sm text-gray-600">{record.date}</span>
                    <span className="font-medium">{record.mileage?.toLocaleString()} miles</span>
                  </div>
                ))}
              </div>
            ) : (
              <pre className="text-xs overflow-auto bg-gray-50 p-3 rounded">{JSON.stringify(data, null, 2)}</pre>
            )}
          </div>
        );

      case "specs":
        return (
          <div className="p-4 grid grid-cols-2 gap-3 text-sm">
            {Object.entries(data).map(([key, value]) => (
              <div key={key} className="flex flex-col">
                <span className="text-gray-500 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                <span className="font-medium">{String(value) || 'N/A'}</span>
              </div>
            ))}
          </div>
        );

      case "valuation":
        return (
          <div className="p-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                £{data.valuation?.toLocaleString() || data.value?.toLocaleString() || 'N/A'}
              </div>
              <div className="text-sm text-gray-500 mt-1">Estimated Market Value</div>
            </div>
            {data.valuationRange && (
              <div className="flex justify-between mt-4 text-sm">
                <div>
                  <span className="text-gray-500">Low: </span>
                  <span className="font-medium">£{data.valuationRange.low?.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">High: </span>
                  <span className="font-medium">£{data.valuationRange.high?.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <pre className="text-xs p-4 overflow-auto bg-gray-50 rounded">{JSON.stringify(data, null, 2)}</pre>
        );
    }
  };

  if (loading) {
    return (
      <Card className="mt-6 border-2">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-600">Loading your purchased data...</p>
        </CardContent>
      </Card>
    );
  }

  // If user already has purchased data for this vehicle
  if (purchasedData && purchasedData.data) {
    return (
      <Card className="mt-6 border-2 border-green-200 bg-green-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Purchased Vehicle Extras
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(purchasedData.data).map(([type, data]) => {
              const Icon = EXTRA_ICONS[type] || FileText;
              const extraInfo = availableExtras.find(e => e.id === type);
              const isExpanded = expandedSections[type];

              return (
                <div key={type} className="border rounded-lg bg-white overflow-hidden">
                  <button
                    onClick={() => toggleSection(type)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">{extraInfo?.name || type}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="border-t">
                      {renderExtraData(type, data)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6 border-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          Want More Details?
        </CardTitle>
        <p className="text-sm text-gray-500">
          Get comprehensive vehicle history, MOT records, and valuation data.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {availableExtras.map((extra) => {
            const Icon = EXTRA_ICONS[extra.id] || FileText;
            const isSelected = selectedExtras.includes(extra.id);
            
            return (
              <div
                key={extra.id}
                onClick={() => toggleExtra(extra.id)}
                className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={isSelected}
                    onCheckedChange={() => toggleExtra(extra.id)}
                  />
                  <Icon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-medium">{extra.name}</div>
                    <div className="text-sm text-gray-500">{extra.description}</div>
                  </div>
                </div>
                <div className={`font-bold ${
                  extra.price >= 100 ? 'text-lg' : 'text-base'
                } ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>
                  {extra.price_display}
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {selectedExtras.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600">Total:</span>
              <span className="text-2xl font-bold">{formatPrice(calculateTotal())}</span>
            </div>
            
            {!user ? (
              <div className="text-center py-4">
                <p className="text-gray-600 mb-3">Please sign in to purchase extras</p>
                <Button variant="outline" onClick={() => window.location.href = '/'}>
                  Sign In
                </Button>
              </div>
            ) : (
              <Button 
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Redirecting to payment...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Purchase - {formatPrice(calculateTotal())}
                  </>
                )}
              </Button>
            )}
            
            <p className="text-xs text-gray-500 text-center mt-2">
              Secure payment powered by Stripe
            </p>
          </div>
        )}

        {isPremium === "enterprise" && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
            <Crown className="h-5 w-5 text-purple-600 mx-auto mb-1" />
            <p className="text-sm text-purple-700">
              As an Enterprise user, all extras are included via API access!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default VehicleExtras;
